import { createRef, memo, useEffect, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { uuid } from '@betty/toolkit/utils';
import { getBottomSheets } from '../utils';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  BottomSheetConfig,
  BottomSheetManagerExposedProps,
} from './BottomSheetsManager.types';
import CustomBottomSheetModal from '../BottomSheetModal/BottomSheetModal';
import { ErrorBoundaryProvider } from '../hoc';

export let BottomSheet: BottomSheetManagerExposedProps = {
  open: () => {},
};

function BottomSheetsManager() {
  const [sheets, setSheets] = useState<BottomSheetConfig[]>([]);
  const sheetsRef = useRef(sheets);

  sheetsRef.current = sheets;

  useEffect(() => {
    BottomSheet = {
      open: (config) => {
        const {
          namespace,
          singleton = true,
          replace,
          onClose: userOnClose,
          ...rest
        } = config;
        const type = config.type as Exclude<
          typeof config.type,
          string | number | symbol
        >;
        const components = getBottomSheets();
        const Component = components[namespace][type];
        const ref = createRef<BottomSheetModal>();
        const id = uuid();

        Keyboard.dismiss();

        if (replace) {
          sheetsRef.current.forEach((sheet) => {
            if (sheet.type === type) {
              sheet.onClose();
            }
          });
        }

        setSheets((sheets) => {
          if (sheets.some((sheet) => sheet.type === type) && singleton) {
            return sheets;
          }

          return [
            ...sheets,
            {
              ...rest,
              id,
              type,
              Component,
              ref,
              onClose: () => {
                userOnClose?.();
                ref.current?.dismiss();
              },
              onDismiss: () => {
                setSheets((sheets) => {
                  const draft = [...sheets];
                  const index = sheets.findIndex((sheet) => sheet.id === id);

                  draft.splice(index, 1);

                  return draft;
                });
              },
            },
          ];
        });
      },
    };
  }, []);

  useEffect(() => {
    if (!sheets.length) {
      return;
    }

    const lastSheet = sheets[sheets.length - 1];

    lastSheet.ref.current?.present();
  }, [sheets]);

  return sheets.map((sheet) => {
    const { id, type, Component, data, onClose, onDismiss, ...rest } = sheet;

    return (
      <CustomBottomSheetModal
        key={id}
        close={onClose}
        onDismiss={onDismiss}
        {...rest}
      >
        <ErrorBoundaryProvider>
          <Component {...data} close={onClose} />
        </ErrorBoundaryProvider>
      </CustomBottomSheetModal>
    );
  });
}

export default memo(BottomSheetsManager);
